import { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';

const STORAGE_KEY = 'applyt:first-message-notice-dismissed';

interface FirstMessageNoticeProps {
  /** Hide the notice once the user has sent at least one message in this session. */
  hidden?: boolean;
}

/**
 * One-time inline disclaimer shown above the chat input before the user's
 * first message. Dismissable with × and remembered in localStorage.
 */
export const FirstMessageNotice = ({ hidden = false }: FirstMessageNoticeProps) => {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(STORAGE_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  if (dismissed || hidden) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Local state still dismisses the notice when storage is unavailable.
    }
    setDismissed(true);
  };

  return (
    <div className="relative z-10 mx-auto mb-3 flex w-full max-w-3xl items-start gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground md:mb-12 md:gap-3 md:px-4 md:py-3">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
      <p className="min-w-0 flex-1 leading-relaxed">
        By messaging Applyt.ai, you agree to our{' '}
        <a href="/terms" className="underline hover:text-foreground">Terms</a> and have read our{' '}
        <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss notice"
        className="-m-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded hover:bg-muted-foreground/10"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
